import { useEffect, useRef, useState } from 'react';

import { Box, Fade, Spinner } from '@chakra-ui/react';
import { User } from 'models';

interface FeedbackChatProps {
  user: User;
  isAvailable?: boolean;
  destroyOnClose?: boolean;
  openOnMount?: boolean;
  onClose?: () => void;
}

const notificationAnimation = `
  @keyframes notify {
    0% {
      opacity: 0;
      transform: scale(0);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const FeedbackChat = ({ user, isAvailable = true, destroyOnClose, openOnMount, onClose }: FeedbackChatProps) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const chatPixelRef = useRef<HTMLDivElement>(null);
  const [gotNewMessage, setGotNewMessage] = useState(false);
  const [isChatUploaded, setIsChatUploaded] = useState(false);
  const [chatElement, setChatElement] = useState<Element | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAvailable) return;

    const findChatElement = () => {
      const element = document.querySelector('.h-open-container');
      if (element) {
        setChatElement(element);
        setIsLoading(false);
        return true;
      }
      return false;
    };

    if (findChatElement()) return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          if (findChatElement()) {
            observer.disconnect();
            break;
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const timeout = setTimeout(() => {
      if (!chatElement) {
        setIsLoading(false);
        console.warn('Chat element was not found within timeout period');
      }
    }, 10000);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [isAvailable, chatElement]);

  useEffect(() => {
    if (chatElement) {
      setIsChatUploaded(true);

      const removeObserver = new MutationObserver((mutations) => {
        if (!document.body.contains(chatElement)) {
          setChatElement(null);
          setIsLoading(true);
        }
      });

      removeObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return () => {
        removeObserver.disconnect();
      };
    }
  }, [chatElement]);

  const removeUneccessaryClasses = () => {
    document.getElementById('fc_frame')?.classList.remove('h-open-notify');
  };

  useEffect(() => {
    if (!isAvailable) return;
    (window as any).fcSettings = {
      onInit: function () {
        (window as any).fcWidget.on('unreadCount:notify', function (resp: { count: number }) {
          setGotNewMessage(resp.count > 0);
          setTimeout(() => {
            removeUneccessaryClasses();
          }, 1000);
        });
        (window as any).fcWidget.on('widget:closed', function () {
          if (onClose) onClose();
        });
      },
    };
  }, [isAvailable]);

  useEffect(() => {
    if (!isAvailable) return;
    (window as any).fcWidgetMessengerConfig = {
      externalId: user.email,
      config: {
        fullscreen: true,
        headerProperty: {
          hideChatButton: true,
          disableNotifications: true,
          disableEvents: true,
        },
      },
    };
  }, [isAvailable]);

  useEffect(() => {
    if (!isAvailable) return;
    const randomHash = Math.random().toString(36).substring(7);
    const script = document.createElement('script');
    const freshchatSource = process.env.REACT_APP_FRESHCHAT_SOURCE || '';
    const version = '6.3.0';
    script.src = `${freshchatSource}?version=${version}&token=${randomHash}`;
    (script as any).chat = true;
    script.id = 'fc_frame_script';

    document.body?.appendChild(script);
  }, [isAvailable]);

  useEffect(() => {
    setTimeout(() => {
      chatPixelRef.current?.click();
      setIsChatUploaded(true);
    }, 1000);
  }, [document.querySelector(`[src="${process.env.REACT_APP_FRESHCHAT_SOURCE}"]`)]);

  const onChatClick = () => {
    if ((window as any).fcWidget) {
      (window as any).fcWidget.user.setEmail(user.email);
      (window as any).fcWidget.user.setFirstName(user.username);
    }
    if ((window as any).fcWidget) {
      (window as any).fcWidget.open();
    }
  };

  useEffect(() => {
    if (isAvailable) {
      document.getElementById('fc_frame')?.style.setProperty('position', 'fixed', 'important');
      document.getElementById('fc_frame')?.style.setProperty('display', 'block', 'important');
      document.getElementById('fc_frame')?.style.setProperty('pointer-events', 'auto', 'important');
      document.getElementById('fc_frame')?.style.setProperty('bottom', '0px', 'important');
      document.getElementById('fc_frame')?.style.setProperty('right', '0px', 'important');
    } else {
      document.querySelectorAll('#fc_frame_script').forEach((el) => el.remove());
      document.getElementById('fc_frame')?.style.setProperty('display', 'none', 'important');
      document.getElementById('fc_frame')?.style.setProperty('bottom', '-100px', 'important');
      document.getElementById('fc_frame')?.style.setProperty('right', '-100px', 'important');
      document.getElementById('fc_frame')?.style.setProperty('pointer-events', 'none', 'important');
    }

    return () => {
      document.querySelectorAll('#fc_frame_script').forEach((el) => el.remove());
      document.getElementById('fc_frame')?.style.setProperty('display', 'none', 'important');

      if (destroyOnClose) {
        document.getElementById('fc_frame')?.remove();
      }
    };
  }, [isAvailable, document.getElementById('fc_frame')]);

  useEffect(() => {
    if (openOnMount && isChatUploaded) {
      onChatClick();
    }
  }, [isChatUploaded]);

  if (!isAvailable) return null;

  return (
    <>
      <Box ref={chatPixelRef} w="1px" h="1px" position="absolute" top="0" left="0" />

      <Fade
        in={isLoading}
        unmountOnExit
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          height: '100%',
          zIndex: '99999999999',
          backgroundColor: '#24242460',
        }}
      >
        <Spinner color="white" size="xl" />
      </Fade>
    </>
  );
};

export default FeedbackChat;
